import React from "react";
import classNames from "classnames";
import { graphql, useStaticQuery } from "gatsby";
import { Link, useI18next } from "gatsby-plugin-react-i18next";

import Container from "./Container";

import FooterLogo from "../assets/new/Footer_Desktop_Gmining_Logo_White.svg";
import YouTubeLogo from "../assets/footer/youtube.svg";
import TwitterLogo from "../assets/footer/twitter.svg";
import LinkedInLogo from "../assets/footer/linkedin.svg";
import FacebookLogo from "../assets/footer/facebook.svg";

const languageNames = {
  "en-US": "ENGLISH",
  fr: "FRANÇAIS",
};

const SocialNetworkLink = ({ link, src, alt }) => {
  return (
    <a href={link} className="mr-28px md:mr-35px last:mr-0 hover:opacity-75">
      <img src={src} alt={alt} className="w-24px h-24px" />
    </a>
  );
};

const SocialNetworkLinksList = ({ footerData }) => {
  return (
    <div className="flex flex-row items-center my-40px md:my-0">
      <SocialNetworkLink link={footerData.youTubeLink} src={YouTubeLogo} alt="YouTube Logo" />
      <SocialNetworkLink link={footerData.twitterLink} src={TwitterLogo} alt="Twitter Logo" />
      <SocialNetworkLink link={footerData.linkedInLink} src={LinkedInLogo} alt="LinkedIn Logo" />
      <SocialNetworkLink link={footerData.facebookLink} src={FacebookLogo} alt="Facebook Logo" />
    </div>
  );
};

const FooterPageLinks = ({ links }) => {
  return (
    <div className={"relative flex flex-col md:flex-row justify-around md:justify-between"}>
      {links
        .filter((link) => link.url !== "/environmental-report")
        .map((link, index) => (
          <div className="flex flex-col w-fit mt-20px first:mt-0 md:mt-0 uppercase" key={`${link.url}-${index}`}>
            <Link to={link.url} className="navlinks text-gold mb-10px hover:underline">
              {link.label}
            </Link>
            <div className="flex flex-col text-cream-white">
              {link.subItems.map((subLink, index) => (
                <Link to={subLink.url} key={`-${link.url}-${index}`} className="mt-10px sublink hover:underline">
                  {subLink.label}
                </Link>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
};

const LanguagePicker = () => {
  const { language, languages, changeLanguage } = useI18next();

  return (
    <div className="flex">
      {languages.map((lang) => (
        <button
          key={lang}
          onClick={() => changeLanguage(lang)}
          className={classNames("text-cream-white first:after:border-r-2 first:after:mx-10px navlinks", {
            "text-gold": language === lang,
            "hover:underline": language !== lang,
          })}
        >
          {languageNames?.[lang]}
        </button>
      ))}
    </div>
  );
};

const Footer = () => {
  const { language } = useI18next();
  const { data } = useStaticQuery(graphql`
    query FooterQuery {
      data: allContentfulHeaderAndFooter {
        nodes {
          mailingListFormTitle
          mailingListFormNameInputLabel
          mailingListFormNameErrorMessage
          mailingListFormEmailInputLabel
          mailingListFormEmailErrorMessage
          mailingListFormRequiredErrorMessage
          mailingListFormSubmitButtonLabel
          socialsTitle
          youTubeLink
          twitterLink
          linkedInLink
          facebookLink
          links {
            label
            url
            subItems {
              label
              url
            }
          }
          copyright
          node_locale
        }
      }
    }
  `);

  const footerData = data?.nodes.filter((data) => data.node_locale === language)[0];

  return (
    <footer className="w-full mx-auto lg:pl-[var(--stock-bar-width)] md:pt-46px">
      <Container outerContainerClassName="bg-pale-blue !py-0" innerContainerClassName="py-40px" additionalPx>
        <div className="flex flex-col">
          <div className="flex flex-col md:flex-row flex-start md:items-center justify-between w-full">
            <div className="flex justify-start items-center">
              <img src={FooterLogo} alt="Footer Logo" className="h-40px md:h-60px w-auto md:w-auto ml-0 md:ml-0" />
            </div>
            <SocialNetworkLinksList footerData={footerData} />
            <LanguagePicker />
          </div>
          <div className="my-40px md:px-144px">
            <div className="w-full h-1px bg-cream-white" />
          </div>
          <FooterPageLinks links={footerData.links} />
          <div className="my-40px md:px-144px">
            <div className="w-full h-1px bg-cream-white" />
          </div>
        </div>
        <div className="w-full text-18px md:text-center leading-[140%] font-lato text-cream-white">
          <span>{footerData.copyright}</span>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
